// This provider is only used in client-side context

import { createContext, useContext, useCallback, useRef, ReactNode } from 'react';

interface UsageContextType {
  trackUsage: (usage: Array<{ featureTag: string; quantity?: number; metadata?: Record<string, unknown> }>) => Promise<void>;
  trackSingleUsage: (featureTag: string, quantity?: number, metadata?: unknown) => Promise<void>;
}

const UsageContext = createContext<UsageContextType | null>(null);

interface UsageTrackingProviderProps {
  children: ReactNode;
  appId: string;
  publicKey: string;
  apiHost: string;
  userId?: string | null | (() => string | null | Promise<string | null>);
}

export function UsageTrackingProvider({
  children,
  appId,
  publicKey,
  apiHost,
  userId
}: UsageTrackingProviderProps) {
  // No longer using auth adapter - userId passed directly

  // Resolve userId helper
  const resolveUserId = useCallback(async (): Promise<string | null> => {
    if (typeof userId === 'function') {
      return await userId();
    }
    return userId || null;
  }, [userId]);

  // Usage batching
  const usageQueue = useRef<Array<{ featureTag: string; quantity: number; metadata?: unknown }>>([]);
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flush usage queue to API
  const flushUsageQueue = useCallback(async () => {
    const resolvedUserId = await resolveUserId();
    if (usageQueue.current.length === 0 || !resolvedUserId) return;

    const currentQueue = [...usageQueue.current];
    usageQueue.current = [];

    try {
      const response = await fetch(`${apiHost}/api/usage/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicKey}`,
          'X-App-Id': appId
        },
        body: JSON.stringify({
          userId: resolvedUserId,
          usage: currentQueue
        })
      });

      if (!response.ok) {
        console.error('UseCelo: Failed to track usage:', response.statusText);
        // Re-queue failed items (optional)
        usageQueue.current.unshift(...currentQueue);
      }
    } catch (error) {
      console.error('UseCelo: Usage tracking error:', error);
      // Re-queue failed items (optional)
      usageQueue.current.unshift(...currentQueue);
    }
  }, [appId, publicKey, apiHost, resolveUserId]);

  // Schedule flush
  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
    }
    flushTimeoutRef.current = setTimeout(flushUsageQueue, 2000);
  }, [flushUsageQueue]);

  // Track usage (batched)
  const trackUsage = useCallback(async (usage: Array<{ featureTag: string; quantity?: number; metadata?: Record<string, unknown> }>) => {
    const resolvedUserId = await resolveUserId();
    if (!resolvedUserId) {
      console.warn('UseCelo: Cannot track usage - no userId provided');
      return;
    }

    // Add to queue
    const normalizedUsage = usage.map(u => ({
      featureTag: u.featureTag,
      quantity: u.quantity || 1,
      metadata: u.metadata
    }));

    usageQueue.current.push(...normalizedUsage);
    scheduleFlush();
  }, [resolveUserId, scheduleFlush]);

  // Track single usage item
  const trackSingleUsage = useCallback(async (featureTag: string, quantity: number = 1, metadata?: unknown) => {
    await trackUsage([{ featureTag, quantity, metadata: metadata as Record<string, unknown> }]);
  }, [trackUsage]);

  const contextValue: UsageContextType = {
    trackUsage,
    trackSingleUsage
  };

  return (
    <UsageContext.Provider value={contextValue}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsageTracking() {
  const context = useContext(UsageContext);
  if (!context) {
    throw new Error('useUsageTracking must be used within UsageTrackingProvider');
  }
  return context;
}